import { Dl, Dt, FormStatus, H3, P } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import { useMedia } from '@dnb/eufemia/shared';
import type { ApiStage, ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import { useRef, useState } from 'react';

import { updateApiLifeCycle } from '@/api/api';
import { ApiLifeCycleRequest, ApiLifeCycleResponse } from '@/openapi/schema';
import FormSubmitButton from '@/pages/profile/apps/application/ApiProviderPage/components/FormSubmitButton';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';

import { FormCancelButton } from '../../../components/FormCancelButton';
import { FormEditButton } from '../../../components/FormEditButton';
import ApiStageTimeLine, { STAGE_OPTIONS, STAGE_ORDER } from './ApiStageTimeLine';

const mapToTimelineStage = (stage: ApiStage): ApiStage => stage;
const toRequestStage = (stage: ApiStage): ApiLifeCycleRequest['stage'] => {
  return mapToTimelineStage(stage).toUpperCase() as ApiLifeCycleRequest['stage'];
};
const fromResponseStage = (stage: ApiLifeCycleResponse['stage']): ApiStage => stage.toLowerCase() as ApiStage;

export type ApiStageTimeLineFormProps = {
  api: ApiWithStatusAndScopesDto;
  isTeamAdmin: boolean;
  isApiChampion: boolean;
  onStageChange: (stage: ApiStage) => void;
};

export default function ApiStageTimeLineForm({
  api,
  isTeamAdmin,
  isApiChampion,
  onStageChange,
}: ApiStageTimeLineFormProps) {
  const { isSmall } = useMedia();

  const canEdit = isTeamAdmin && isApiChampion;
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const switchContainerModeRef = useRef<SwitchContainerMode>(undefined);

  const currentStage = mapToTimelineStage(api.stage);
  const formId = `api-stage-timeline-${api.slug}`;
  const { getValue, update } = Form.useData(formId);
  const formPath = '/lifecycle/stage';
  const resetLocal = () => update(formPath, currentStage);

  const handleStartEdit = () => {
    setErrorMessage(null);
    setIsEditing(true);
    resetLocal();
  };

  const handleCancel = () => {
    setIsEditing(false);
    resetLocal();
    setErrorMessage(null);
    switchContainerModeRef.current?.('view');
  };

  const handleSelectStage = (stage: ApiStage) => {
    if (!canEdit || !isEditing || isSaving) return;
    update(formPath, stage);
  };

  const selectedStage = (getValue(formPath) as ApiStage) ?? currentStage;
  const hasChanged = selectedStage !== currentStage;

  const handleSave = async () => {
    if (!hasChanged || isSaving) return;
    setErrorMessage(null);
    setIsSaving(true);
    const { data, error } = await updateApiLifeCycle(api.slug, { stage: toRequestStage(selectedStage) });
    setIsSaving(false);
    if (error) {
      setErrorMessage(error.detail ?? 'Failed to update API stage');
      return;
    }
    if (data?.stage) {
      const newStage = fromResponseStage(data.stage);
      onStageChange(newStage);
    }
    setIsEditing(false);
    switchContainerModeRef.current?.('view');
  };

  return (
    <>
      <H3 bottom="small">API Stage</H3>

      <Form.Handler defaultData={{ lifecycle: { stage: currentStage } }} id={formId}>
        <Form.Section>
          <Form.Section.ViewContainer bottom="small">
            {isSmall ? (
              <Dl top="medium">
                <Dt>{STAGE_OPTIONS[currentStage]}</Dt>
              </Dl>
            ) : (
              <ApiStageTimeLine activeStage={currentStage} interactive={false} />
            )}
            <Form.Section.Toolbar>
              <FormEditButton
                disabled={!canEdit}
                disabledTooltip={
                  isTeamAdmin ? (
                    isApiChampion ? undefined : (
                      <P>Only API Champions have access to edit</P>
                    )
                  ) : (
                    <P>Only team admins have access to edit</P>
                  )
                }
                onEdit={handleStartEdit}
                switchContainerModeRef={switchContainerModeRef}
              />
            </Form.Section.Toolbar>
          </Form.Section.ViewContainer>

          <Form.Section.EditContainer bottom="small">
            {isSmall ? (
              <Field.Selection
                disabled={!canEdit || isSaving}
                onChange={(value) => handleSelectStage(value as ApiStage)}
                path={formPath}
                top="medium"
                variant="dropdown"
              >
                {STAGE_ORDER.map((stage) => (
                  <Field.Option key={stage} title={STAGE_OPTIONS[stage]} value={stage} />
                ))}
              </Field.Selection>
            ) : (
              <ApiStageTimeLine
                activeStage={selectedStage}
                interactive={canEdit}
                isSaving={isSaving}
                onSelect={handleSelectStage}
              />
            )}
            <Form.Section.Toolbar>
              <FormSubmitButton
                disabled={!hasChanged || isSaving}
                onClick={handleSave}
                switchContainerModeRef={switchContainerModeRef}
              />
              <FormCancelButton onCancel={handleCancel} switchContainerModeRef={switchContainerModeRef} />
            </Form.Section.Toolbar>
            {errorMessage && <FormStatus state="error" stretch text={errorMessage} top="small" />}
          </Form.Section.EditContainer>
        </Form.Section>
      </Form.Handler>
    </>
  );
}
